// Copy for the unified pipeline-health banner (Quote Assessment). Pure; no React.
// One block per non-healthy upstream. Two severities: DOWN (offline, red) and DEGRADED
// (answering but thin, amber). Voice (matches soldLaneStatus.js): lead with what
// happened, name a checkable date in UK house style, no em dashes, no exclamation marks.
import { formatUkDateTime } from './soldLaneStatus.js';

const COPY = {
    sold: {
        down: {
            title: 'eBay sold prices are not answering',
            body: (when) => when
                ? `Our check search has found no eBay sold comps since ${when}. Quotes priced since then use asking prices and Reverb sales only, so check them before you send.`
                : 'Our check search is finding no eBay sold comps at the moment. Quotes priced while it is down use asking prices and Reverb sales only, so check them before you send.',
        },
        degraded: {
            title: 'eBay sold prices are thin',
            body: (when) => `Our check search is finding far fewer eBay sold comps than it normally does${when ? `, since ${when}` : ''}. Prices still use what comes back, so check borderline ones before you send.`,
        },
    },
    vision: {
        down: {
            title: 'Item identification is offline',
            body: (when) => `Quotes ${when ? `priced since ${when} ` : ''}are built from the seller's description, not the photos, so check them before you send.`,
        },
    },
    reverb: {
        down: {
            title: 'Reverb prices are offline',
            body: (when) => when
                ? `Reverb has not answered since ${when}. Quotes priced since then use eBay and Google data only, so check them before you send.`
                : 'Reverb is not answering at the moment. Quotes priced while it is down use eBay and Google data only, so check them before you send.',
        },
    },
    serpapi: {
        down: {
            title: 'eBay asking prices and Google Shopping prices are offline',
            body: (when) => when
                ? `SerpApi has not answered since ${when}. Quotes priced since then are missing eBay asking prices and Google Shopping prices, so reprice them before you send.`
                : 'SerpApi is not answering at the moment. Quotes priced while it is down are missing eBay asking prices and Google Shopping prices, so reprice them before you send.',
        },
    },
    ocr: {
        down: {
            title: 'Serial and label reading is offline',
            body: (when) => `Serial numbers and printed model details may be missed${when ? ` on quotes priced since ${when}` : ''}, so check them before you send.`,
        },
    },
    classifier: {
        down: {
            title: 'Comp classification is failing',
            body: (when) => when
                ? `The comp classifier has not answered since ${when}. Quotes priced since then are missing market comps, so reprice them before you send.`
                : 'The comp classifier is not answering at the moment. Quotes priced while it is down are missing market comps, so reprice them before you send.',
        },
        degraded: {
            title: 'Some market comps are being dropped',
            body: (when) => `The comp classifier timed out on some of its recent calls${when ? ` since ${when}` : ''}. Affected quotes show 'comps dropped' on the item and need a reprice.`,
        },
    },
};

// status is the /api/ops/pipeline-health body: { services: [{ key, state, since, detail }] }.
// environment is the /api/environment name ('production' | 'staging'), or undefined while loading.
// Returns [{ key, severity, title, body, detail }] for each non-healthy service that has copy.
export function pipelineBannerItems(status, environment) {
    if (!status || !Array.isArray(status.services)) return [];
    // Neutral note (not a fault), staging only: staging never holds our eBay session, so its sold
    // comps always come from Apify. In production Apify is a deliberate setting, so no note there.
    const apify = environment === 'staging'
        && status.services.some((s) => s?.key === 'sold' && s.state === 'healthy' && s.source === 'apify')
        ? [{
            key: 'sold-source',
            severity: 'info',
            title: 'eBay sold prices are coming from Apify',
            body: 'Our own eBay session is not in use here. Sold comps still flow.',
            detail: null,
        }]
        : [];
    return apify.concat(status.services
        .filter((s) => (s?.state === 'down' || s?.state === 'degraded') && COPY[s.key] && COPY[s.key][s.state])
        .map((s) => {
            const c = COPY[s.key][s.state];
            return {
                key: s.key,
                severity: s.state,
                title: c.title,
                body: c.body(formatUkDateTime(s.since)),
                detail: s.detail || null,
            };
        }));
}
